import {
    AdvancedFilterGroupNode,
    AdvancedFilterOverlappNode,
    appendAdvancedFilterNodeToClause,
    removeAdvancedFilterNode,
    replaceAdvancedFilterNode,
    updateAdvancedFilterClauseNegated,
    updateAdvancedFilterGroupNegated,
} from '@/domain/advancedFilter';
import { Vegobjekttype } from '@/domain/vegobjekter/Vegobjekttype';
import { VegobjekttypeState } from '@/state';
import { UserState } from '@/state/UserState';
import * as React from 'react';
import { AddButtons, createNodeForAddAction } from './AdvancedFilterAddButtons';
import { AdvancedFilterGroupEditor } from './AdvancedFilterGroupEditor';
import { AdvancedFilterTextDropdownInput } from './AdvancedFilterTextDropdownInput';

interface Props {
    root: AdvancedFilterGroupNode;
    node: AdvancedFilterOverlappNode;
    nodeDepth: number;
    vegobjekttypeData: VegobjekttypeState;
    datakatalogVegobjekttyper: Vegobjekttype[];
    errorsByNodeId: Record<string, string[]>;
    onDraftChange: (draft: AdvancedFilterGroupNode | null) => void;
    user: UserState;
    disableSuggestedValueFetch?: boolean;
}

export const OverlappClauseEditor: React.FC<Props> = ({
    root,
    node,
    nodeDepth,
    vegobjekttypeData,
    datakatalogVegobjekttyper,
    errorsByNodeId,
    onDraftChange,
    user,
    disableSuggestedValueFetch = false,
}) => {
    const [searchText, setSearchText] = React.useState(
        typeLabel(node.targetTypeId, datakatalogVegobjekttyper)
    );

    React.useEffect(() => {
        setSearchText(typeLabel(node.targetTypeId, datakatalogVegobjekttyper));
    }, [datakatalogVegobjekttyper, node.targetTypeId]);

    const selectedType = node.targetTypeId
        ? (datakatalogVegobjekttyper.find(type => type.id === node.targetTypeId) ?? null)
        : null;

    const handleTypeCommit = (value: string) => {
        setSearchText(value);
        const targetTypeId = parseTypeValue(value, datakatalogVegobjekttyper);
        onDraftChange(
            replaceAdvancedFilterNode(root, node.id, currentNode => ({
                ...currentNode,
                targetTypeId,
                child: targetTypeId === node.targetTypeId ? node.child : null,
            }))
        );
    };

    const handleNegationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const negated = event.target.value === 'without';
        onDraftChange(updateAdvancedFilterClauseNegated(root, node.id, negated));
    };

    const handleChildNegationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (!node.child) {
            return;
        }

        const negated = event.target.value === 'without';
        onDraftChange(updateAdvancedFilterGroupNegated(root, node.child.id, negated));
    };

    const handleRemoveChild = () => {
        if (!node.child) {
            return;
        }
        onDraftChange(removeAdvancedFilterNode(root, node.child.id));
    };

    const childContainerDepth = nodeDepth + 1;
    const handleAddNestedNode = React.useCallback(
        (kind: Parameters<typeof createNodeForAddAction>[0]) => {
            onDraftChange(
                appendAdvancedFilterNodeToClause(
                    root,
                    node.id,
                    createNodeForAddAction(kind)
                )
            );
        },
        [node.id, onDraftChange, root]
    );

    const childTypeProperties = selectedType?.egenskapstyper ?? [];
    const objectTypeOptions = React.useMemo(
        () => datakatalogVegobjekttyper.map(type => `${type.id} - ${type.navn}`),
        [datakatalogVegobjekttyper]
    );

    return (
        <>
            <div className="c-advanced-filter__node-header c-advanced-filter__node-header--compact">
                <div
                    className="c-advanced-filter__node-kind c-advanced-filter__node-kind--plain"
                    title="Filtrer på overlappende stedfesting for vegobjekter av en annen gitt type"
                >
                    <i
                        className="material-icons c-advanced-filter__node-kind-icon"
                        aria-hidden="true"
                    >
                        join_inner
                    </i>
                    <select
                        className="c-advanced-filter__input c-advanced-filter__input--inline c-advanced-filter__input--negation"
                        value={node.negated ? 'without' : 'with'}
                        onChange={handleNegationChange}
                        aria-label="Velg om du vil vise objekter med eller uten overlapp med valgt objekttype"
                        title="Velg med eller uten overlapp med valgt objekttype"
                    >
                        <option value="with">Overlapp</option>
                        <option value="without">Uten overlapp</option>
                    </select>
                </div>
                <AdvancedFilterTextDropdownInput
                    value={searchText}
                    options={objectTypeOptions}
                    filterOptions={filterObjectTypeOptions}
                    onValueChange={setSearchText}
                    onCommitValue={handleTypeCommit}
                    placeholder="Velg objekttype"
                    ariaLabel="Velg vegobjekttype for overlapp"
                    dropdownAriaLabel="Forslag til objekttyper for overlapp"
                    inputClassName="c-advanced-filter__input c-advanced-filter__input--inline"
                    title={searchText || 'Velg objekttype'}
                    emptyText="Finner ingen objekttyper"
                />
            </div>

            {selectedType &&
                (node.child ? (
                    <div className="c-advanced-filter__nested c-advanced-filter__nested--overlapp">
                        <div className="c-advanced-filter__nested-header">
                            <div className="c-advanced-filter__nested-header-main">
                                <div
                                    className="c-advanced-filter__group-title"
                                    title={`Underfilter gjelder for ${selectedType.navn}`}
                                >
                                    <i
                                        className="material-icons c-advanced-filter__node-kind-icon"
                                        aria-hidden="true"
                                    >
                                        workspaces
                                    </i>
                                    <select
                                        className="c-advanced-filter__input c-advanced-filter__input--inline c-advanced-filter__input--negation"
                                        value={node.child.negated ? 'without' : 'with'}
                                        onChange={handleChildNegationChange}
                                        aria-label={`Velg om underfilteret for ${selectedType.navn} skal finne treff med eller uten det som er valgt i underfilteret`}
                                        title={`Velg med eller uten det som er valgt i underfilteret for ${selectedType.navn}`}
                                    >
                                        <option value="with">Underfilter</option>
                                        <option value="without">Underfilter uten</option>
                                    </select>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="c-advanced-filter__remove-button"
                                onClick={handleRemoveChild}
                            >
                                Fjern
                            </button>
                        </div>
                        <AdvancedFilterGroupEditor
                            root={root}
                            group={node.child}
                            groupDepth={childContainerDepth}
                            isRoot
                            egenskapstyper={childTypeProperties}
                            vegobjekttypeData={vegobjekttypeData}
                            datakatalogVegobjekttyper={datakatalogVegobjekttyper}
                            errorsByNodeId={errorsByNodeId}
                            onDraftChange={onDraftChange}
                            user={user}
                            disableSuggestedValueFetch={disableSuggestedValueFetch}
                        />
                    </div>
                ) : (
                    <AddButtons
                        onAdd={handleAddNestedNode}
                        containerDepth={childContainerDepth}
                    />
                ))}
        </>
    );
};

function typeLabel(targetTypeId: number | null, vegobjekttyper: Vegobjekttype[]): string {
    if (!targetTypeId) {
        return '';
    }

    const vegobjekttype = vegobjekttyper.find(type => type.id === targetTypeId);
    return vegobjekttype
        ? `${vegobjekttype.id} - ${vegobjekttype.navn}`
        : String(targetTypeId);
}

function parseTypeValue(value: string, vegobjekttyper: Vegobjekttype[]): number | null {
    if (!value) {
        return null;
    }

    const exact = vegobjekttyper.find(type => `${type.id} - ${type.navn}` === value);
    if (exact) {
        return exact.id;
    }

    const leadingNumber = /^\s*(\d+)/.exec(value);
    return leadingNumber ? Number.parseInt(leadingNumber[1], 10) : null;
}

function filterObjectTypeOptions(inputValue: string, options: string[]): string[] {
    if (!inputValue) {
        return options;
    }

    const loweredInput = inputValue.toLowerCase();
    return options.filter(option => option.toLowerCase().includes(loweredInput));
}
